/**
 * Screen 2 — Panel / dashboard.
 *
 * Four business KPIs across the top, the recent documents on the left, and a
 * right rail carrying the per-estado summary and the two quick actions — the
 * shape of the `Portal · Panel` frame.
 *
 * The month figures and their trend come from three calls to `/decas/stats`:
 * unscoped, this month, last month. The endpoint takes date bounds, so the
 * comparison Figma draws needed no contract change. They bound when a document
 * was created, not its service date as the list's do: a month KPI counts what
 * was created in it. A month whose predecessor was empty shows no trend chip
 * rather than an unfoundable percentage.
 *
 * `expiringSoon` counts the documents whose direct download closes within **48
 * hours**. Figma draws seven days; the window is fixed in the contract's prose
 * and computed in the backend, so the number is what it is and the frame was
 * corrected instead.
 *
 * ⚠️ **This screen counts goods DeCAs only, and says so on purpose.** Every
 * figure here comes from `/decas/stats` and `/decas`; no call is made to
 * `/passenger-decas/stats`, which the frozen contract does declare. So
 * `panel.kpiMonth`, `panel.recent` and `panel.colDeca` were left naming DeCAs
 * when the rest of the portal went neutral for route sheets: the labels match
 * what the numbers are, and neutralising the words alone would turn an accurate
 * goods count into an undercount of "documents". Whether the Panel should count
 * route sheets at all is a product decision — an operator running both services
 * sees one of them missing from their month — and it was never in this branch's
 * scope. It needs one more query per KPI, a second recent table or a merged one,
 * and the labels changing with them.
 *
 * `panel.quickCreate` is the exception, and for a different reason: it is not a
 * count but a link, and since the create screen was split it opens the chooser
 * that offers both types. It now reads "Crear documento", the same words the nav
 * entry to that screen uses.
 */

import { useMemo } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { DECA_ESTADOS, type DecaSummary } from '@deca/api'

import { useDecaList, useDecaStats } from '../app/queries.ts'
import { useAuth } from '../app/auth.tsx'
import { abilitiesFor } from '../deca/roles.ts'
import { estadoPluralKey, estadoStyle } from '../deca/estado.ts'
import { formatServiceDate, monthWindow, percentChange } from '../deca/format.ts'
import { AppShell } from '../shell/AppShell.tsx'
import { Button, Card, Chip, ErrorState, LoadingRows } from '../ui/primitives.tsx'
import { DataTable, type Column } from '../ui/DataTable.tsx'
import { EstadoChip, GroupedTag } from '../ui/EstadoChip.tsx'
import { Icon } from '../ui/Icon.tsx'

export function PanelScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { principal } = useAuth()
  const abilities = abilitiesFor(principal)

  // One window per render pass, not one per call: three queries keyed on
  // `new Date()` would each get their own boundary and refetch on every tick.
  const thisMonth = useMemo(() => monthWindow(0), [])
  const lastMonth = useMemo(() => monthWindow(-1), [])

  const stats = useDecaStats()
  const monthStats = useDecaStats(thisMonth)
  const previousStats = useDecaStats(lastMonth)
  const recent = useDecaList({ perPage: 5, sort: '-createdAt' })

  const trend = percentChange(monthStats.data?.total ?? 0, previousStats.data?.total ?? 0)

  // The Panel's headers are the short ones Figma prints — "DeCA", "Fecha" —
  // not the list's precise "Identificador" / "Fecha de realización". A table
  // column is at least as wide as its header, and the long forms were pushing
  // Estado, the column a glance is actually for, off the card.
  const columns: Column<DecaSummary>[] = [
    {
      key: 'documentNumber',
      header: t('panel.colDeca'),
      numeric: true,
      render: (row) => (
        // Wrapping, so the grouped tag drops under the number instead of
        // holding the column open at the width of the two side by side.
        <span className="row wrap" style={{ gap: 'var(--space-sm)' }}>
          <span className="doc-number">{row.documentNumber ?? t('estado.BORRADOR')}</span>
          {row.isGrouped ? <GroupedTag count={row.consignmentCount} /> : null}
        </span>
      ),
    },
    {
      key: 'cargador',
      header: t('list.columns.cargador'),
      render: (row) => (
        <span className="truncate" style={{ display: 'block', maxWidth: 130 }}>
          {row.cargador_razon_social}
        </span>
      ),
    },
    {
      key: 'route',
      header: t('list.columns.route'),
      render: (row) => (
        <span className="truncate" style={{ display: 'block', maxWidth: 155 }}>
          {t('list.routeSummary', { origen: row.origen_resumen, destino: row.destino_resumen })}
        </span>
      ),
    },
    {
      key: 'fecha',
      header: t('common.date'),
      numeric: true,
      render: (row) => formatServiceDate(row.fecha_realizacion, row.fechaRealizacionHasTime, i18n.language),
    },
    {
      // No `voidReason` here, unlike the list: the cause wraps onto a second
      // and third line and turns one row of a five-row summary into three. It
      // is one click away on the document, where it is not competing for space.
      key: 'estado',
      header: t('list.columns.estado'),
      // On a phone's card (#111), in the heading after the number, as on the list's cards.
      card: 'title',
      render: (row) => <EstadoChip estado={row.estado} />,
    },
  ]

  return (
    <AppShell title={t('panel.title')}>
      {stats.error ? (
        <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
      ) : stats.isLoading ? (
        <LoadingRows rows={1} columns={4} />
      ) : (
        <>
          <section aria-label={t('panel.kpiSection')} className="kpi-row">
            <Kpi
              label={t('panel.kpiMonth')}
              value={monthStats.data?.total ?? 0}
              chip={
                // Every card carries a chip, so this one does too even when
                // there is no percentage to state. A previous month of zero has
                // none — every increase from it is infinite — and printing
                // "0 %" there would claim the month was flat when it was the
                // first. The chip says there is no baseline instead.
                trend === null
                  ? { tone: 'neutral', text: t('panel.trendNoBaseline') }
                  : {
                      tone: trend >= 0 ? 'info' : 'warning',
                      // A flat month is information; "+0 %" is not — the sign
                      // only appears when there is a direction to point in.
                      text: t('panel.trend', {
                        sign: trend > 0 ? '+' : trend < 0 ? '−' : '',
                        value: Math.abs(trend),
                      }),
                    }
              }
              onClick={() =>
                navigate({
                  to: '/decas',
                  search: { fechaDesde: thisMonth.fechaDesde, fechaHasta: thisMonth.fechaHasta },
                })
              }
            />

            <Kpi
              label={t('estadoPlural.EN_CURSO')}
              value={stats.data?.byEstado?.['EN CURSO'] ?? 0}
              chip={{ tone: 'info', text: t('panel.chipActiveNow') }}
              onClick={() => navigate({ to: '/decas', search: { estado: ['EN CURSO'] } })}
            />

            <Kpi
              label={t('panel.kpiFinishedMonth')}
              value={monthStats.data?.byEstado?.FINALIZADO ?? 0}
              chip={{ tone: 'success', text: t('panel.chipUpToDate') }}
              onClick={() =>
                navigate({
                  to: '/decas',
                  search: {
                    estado: ['FINALIZADO'],
                    fechaDesde: thisMonth.fechaDesde,
                    fechaHasta: thisMonth.fechaHasta,
                  },
                })
              }
            />

            {/* No filter expresses "download about to close", so this one reads
                rather than navigates — a card that looks clickable and lands on
                an unfiltered list would be worse than one that does not. */}
            <Kpi
              label={t('panel.expiringSoon')}
              value={stats.data?.expiringSoon ?? 0}
              chip={{ tone: 'warning', text: t('panel.chipReview') }}
              hint={t('panel.expiringSoonHelp')}
            />
          </section>

          <div className="panel-layout">
            <Card
              id="recent"
              title={t('panel.recent')}
              actions={
                <Link to="/decas" search={{}} className="btn btn--ghost btn--sm">
                  {t('panel.viewAll')}
                  <Icon name="chevronRight" size={14} />
                </Link>
              }
              flush
            >
              <DataTable
                rows={recent.data?.data}
                columns={columns}
                rowKey={(row) => row.id}
                caption={t('panel.recent')}
                isLoading={recent.isLoading}
                error={recent.error}
                onRetry={() => void recent.refetch()}
                onRowClick={(row) => navigate({ to: '/decas/$decaId', params: { decaId: row.id } })}
              />
            </Card>

            <div className="stack-lg">
              <Card title={t('panel.byEstado')}>
                <ul className="estado-legend">
                  {DECA_ESTADOS.map((estado) => (
                    <li key={estado}>
                      <Link
                        to="/decas"
                        search={{ estado: [estado] }}
                        className="estado-legend__row"
                      >
                        <span
                          className="estado-legend__dot"
                          data-tone={estadoStyle(estado).tone}
                          aria-hidden="true"
                        />
                        <span className="estado-legend__label">{t(estadoPluralKey(estado))}</span>
                        <span className="estado-legend__count tabular">
                          {stats.data?.byEstado?.[estado] ?? 0}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Card>

              {abilities.writeDecas ? (
                <Card title={t('panel.quickActions')}>
                  <div className="stack-sm">
                    <Button
                      variant="primary"
                      icon="plus"
                      onClick={() => void navigate({ to: '/decas/nueva' })}
                    >
                      {t('panel.quickCreate')}
                    </Button>
                    <Button icon="upload" onClick={() => void navigate({ to: '/importar' })}>
                      {t('panel.quickImport')}
                    </Button>
                  </div>
                </Card>
              ) : null}
            </div>
          </div>
        </>
      )}
    </AppShell>
  )
}

function Kpi({
  label,
  value,
  chip,
  hint,
  onClick,
}: {
  label: string
  value: number
  chip?: { tone: 'info' | 'success' | 'warning' | 'neutral'; text: string } | null
  hint?: string
  onClick?: () => void
}) {
  const body = (
    <>
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">{value}</span>
      {chip ? (
        <Chip tone={chip.tone} muted>
          {chip.text}
        </Chip>
      ) : null}
      {/* The `title` below is a mouse's; a finger cannot hover. `responsive/lists.css` shows the
          same sentence here wherever the touch tier applies, and hides it everywhere else (#111). */}
      {hint ? <span className="small muted kpi__hint">{hint}</span> : null}
    </>
  )

  if (!onClick) {
    return (
      <div className="kpi kpi--static" title={hint}>
        {body}
      </div>
    )
  }

  return (
    <button type="button" className="kpi" onClick={onClick} title={hint}>
      {body}
    </button>
  )
}
